import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import {
  buildMyAppsSidebarLeaves,
  CookieConsentBanner,
  launchMyAppsEntry,
  MyAppsGlobalExperience,
  MyAppsRecentMenu,
  openExternalAppUrl,
  PlatformAppShell,
  resolveRecentMyAppId,
  showErrorToast,
  useActivityHeartbeat,
  useEditorWorkspaceStore,
  useMyAppsEntries,
  useMyAppsStore,
  useSiteSupportPopups,
  WEBOPS_MY_APPS_REGISTRY,
  type HeaderUserMenuItem,
  type MyAppsSearchResult
} from '@jarvis/platform-ui';
import { signoutUrl } from '../../api/auth';
import { useAuthStore } from '../../auth/useAuthStore';
import { useHasRole } from '../../auth/useHasRole';
import { COOKIE_POLICY_VERSION, SITE_SUPPORT_COPY, siteFeedbackApi } from '../../siteSupport/config';
import { useShellPreferencesStore } from '../../shell/useShellPreferencesStore';
import { useThemeStore } from '../../theme/useThemeStore';
import { IT_NAV_ITEMS } from '../../navigation/itNavItems';
import { filterNav, type OpsNavLeaf } from '../../navigation/filterNav';
import './AppShell.css';

const NAV_ITEMS: OpsNavLeaf[] = [
  { id: 'dashboard', text: 'Dashboard', path: '/' },
  {
    id: 'files',
    text: 'Files',
    items: [
      { id: 'files-browser', text: 'File Browser', path: '/files' },
      { id: 'files-workspace', text: 'Workspace', path: '/files/workspace' }
    ]
  },
  {
    id: 'secure-pharm-reports',
    text: 'Secure Pharm Reports',
    path: '/finance/secure-pharm-reports',
    securePharmReportsAccess: true
  },
  {
    id: 'hr',
    text: 'Human Relations',
    hrSection: true,
    items: [
      {
        id: 'company-employees',
        text: 'Company Employees',
        path: '/hr/company-employees',
        hrSection: true
      },
      {
        id: 'time-off',
        text: 'Time Off Tracker',
        hrSection: true,
        items: [
          { id: 'time-off-calendar', text: 'Calendar View', path: '/hr/time-off/calendar', hrSection: true },
          { id: 'time-off-all', text: 'Manage Existing PTO', path: '/hr/time-off/all', hrSection: true }
        ]
      },
      {
        id: 'employee-offboarding',
        text: 'Employee Offboarding',
        role: 'ItSysAdmin',
        items: [
          {
            id: 'offboard-employee',
            text: 'Offboard Employee',
            path: '/hr/offboarding/offboard',
            role: 'ItSysAdmin'
          },
          {
            id: 'offboarding-history',
            text: 'Offboarding History',
            path: '/hr/offboarding/history',
            role: 'ItSysAdmin'
          }
        ]
      }
    ]
  },
  // Sales CE tools are not released for OpsSales yet — hide nav for that role.
  // ItSysAdmin + OpsOperations (ops admin) still see this block.
  {
    id: 'sales',
    text: 'Sales',
    unreleasedDepartmentAccess: true,
    items: [
      {
        id: 'ce-clients',
        text: 'CE Clients List',
        path: '/sales/ce-clients',
        unreleasedDepartmentAccess: true
      },
      {
        id: 'ce-client-access',
        text: 'CE Client Access',
        path: '/sales/ce-client-access',
        unreleasedDepartmentAccess: true
      }
    ]
  },
  // Pharmacy Ops tools are not released for OpsPharmacy yet — hide nav for that role.
  // ItSysAdmin + OpsOperations (ops admin) still see this block.
  {
    id: 'pharmacy',
    text: 'Pharmacy Operations',
    unreleasedDepartmentAccess: true,
    items: [
      {
        id: 'ce-patient-tracking',
        text: 'CE Patient Tracking',
        path: '/pharmacy/ce-patient-tracking',
        unreleasedDepartmentAccess: true
      },
      {
        id: 'patient-tracking-info',
        text: 'Patient Tracking Info',
        unreleasedDepartmentAccess: true,
        items: [
          {
            id: 'covered-entity-list',
            text: 'Covered Entity List',
            path: '/pharmacy/covered-entity-list',
            unreleasedDepartmentAccess: true
          },
          {
            id: 'patients',
            text: 'Patient List',
            path: '/pharmacy/patients',
            unreleasedDepartmentAccess: true
          },
          {
            id: 'alerting-history',
            text: 'Alerting History',
            path: '/pharmacy/alerting-history',
            unreleasedDepartmentAccess: true
          }
        ]
      }
    ]
  },
  {
    id: 'information-technology',
    text: 'Information Technology',
    role: 'ItSysAdmin',
    items: IT_NAV_ITEMS
  },
  {
    id: 'ops-admin',
    text: 'Site Administration',
    role: 'ItSysAdmin',
    items: [
      {
        id: 'admin-access-tracking',
        text: 'Access Tracking',
        role: 'ItSysAdmin',
        items: [
          {
            id: 'admin-hub-access-tracking',
            text: 'HVG Hub Access Tracking',
            role: 'ItSysAdmin',
            items: [
              {
                id: 'admin-user-activity',
                text: 'User Activity',
                path: '/admin/user-activity',
                role: 'ItSysAdmin'
              }
            ]
          },
          {
            id: 'admin-client-access-tracking',
            text: 'Client Access Tracking',
            role: 'ItSysAdmin',
            items: [
              {
                id: 'admin-client-portal-user-activity',
                text: 'User Activity',
                path: '/admin/client-portal/user-activity',
                role: 'ItSysAdmin'
              },
              {
                id: 'admin-client-portal-tester-omit-accounts',
                text: 'Tester Omit Accounts',
                path: '/admin/client-portal/tester-omit-accounts',
                role: 'ItSysAdmin'
              }
            ]
          }
        ]
      },
      {
        id: 'admin-calendar-sync',
        text: 'Calendar & Sync',
        role: 'ItSysAdmin',
        items: [
          {
            id: 'admin-calendar-targets',
            text: 'Calendar Targets',
            path: '/admin/time-off/calendar-targets',
            role: 'ItSysAdmin'
          },
          {
            id: 'admin-company-holidays',
            text: 'Company Holidays',
            path: '/admin/time-off/company-holidays',
            role: 'ItSysAdmin'
          },
          {
            id: 'admin-time-off-sync-logs',
            text: 'Calendar Sync Logs',
            path: '/admin/time-off/sync-logs',
            role: 'ItSysAdmin'
          }
        ]
      },
      {
        id: 'admin-feedback-group',
        text: 'Site Feedback',
        role: 'ItSysAdmin',
        items: [
          { id: 'admin-bugs', text: 'Bug Reports', path: '/admin/bugs', role: 'ItSysAdmin' },
          { id: 'admin-feedback', text: 'User Feedback', path: '/admin/feedback', role: 'ItSysAdmin' },
          {
            id: 'admin-support-metadata',
            text: 'Support Metadata',
            path: '/admin/support-metadata',
            role: 'ItSysAdmin'
          },
          {
            id: 'admin-dashboard-tips',
            text: 'Tips & Tricks',
            role: 'ItSysAdmin',
            items: [
              {
                id: 'admin-hub-dashboard-tips',
                text: 'HVG Hub',
                path: '/admin/dashboard-tips',
                role: 'ItSysAdmin'
              },
              {
                id: 'admin-client-portal-dashboard-tips',
                text: 'Client Portal',
                path: '/admin/client-portal/dashboard-tips',
                role: 'ItSysAdmin'
              }
            ]
          }
        ]
      },
      {
        id: 'admin-storage',
        text: 'Site Storage',
        role: 'ItSysAdmin',
        items: [
          {
            id: 'admin-new-client-space',
            text: 'New Client Space',
            path: '/admin/new-client-space',
            role: 'ItSysAdmin'
          },
          {
            id: 'admin-hub-storage',
            text: 'HVG Hub Storage',
            role: 'ItSysAdmin',
            items: [
              {
                id: 'admin-storage-mounts',
                text: 'Storage Mounts',
                path: '/admin/storage-mounts',
                role: 'ItSysAdmin'
              },
              {
                id: 'admin-storage-file-audit',
                text: 'File Operation Audit',
                path: '/admin/storage-file-audit',
                role: 'ItSysAdmin'
              }
            ]
          },
          {
            id: 'admin-client-storage',
            text: 'Client Storage',
            role: 'ItSysAdmin',
            items: [
              {
                id: 'admin-client-portal-storage-mounts',
                text: 'Storage Mounts',
                path: '/admin/client-portal/storage-mounts',
                role: 'ItSysAdmin'
              },
              {
                id: 'admin-client-portal-storage-file-audit',
                text: 'File Operation Audit',
                path: '/admin/client-portal/storage-file-audit',
                role: 'ItSysAdmin'
              }
            ]
          }
        ]
      },
      {
        id: 'admin-client-accounts',
        text: 'Client Accounts',
        role: 'ItSysAdmin',
        items: [
          {
            id: 'admin-hvg-ce-groups',
            text: 'HVG CE Groups',
            path: '/admin/client-accounts/ce-groups',
            role: 'ItSysAdmin'
          },
          {
            id: 'admin-hvg-clients',
            text: 'HVG Clients',
            path: '/admin/client-accounts/clients',
            role: 'ItSysAdmin'
          },
          {
            id: 'admin-client-offboarding-notify',
            text: 'Off-boarding Notify',
            path: '/admin/client-accounts/offboarding-notify',
            role: 'ItSysAdmin'
          },
          {
            id: 'admin-client-license-summary',
            text: 'License Summary',
            path: '/admin/client-accounts/license-summary',
            role: 'ItSysAdmin'
          }
        ]
      },
      {
        id: 'admin-system',
        text: 'System',
        role: 'ItSysAdmin',
        items: [
          { id: 'admin-sync-jobs', text: 'Sync Jobs', path: '/admin/sync-jobs', role: 'ItSysAdmin' },
          {
            id: 'admin-mentions-system',
            text: 'Mentions System',
            role: 'ItSysAdmin',
            items: [
              {
                id: 'admin-client-mentions',
                text: 'Client Mentions',
                path: '/admin/site-feedback/client-mentions',
                role: 'ItSysAdmin'
              },
              {
                id: 'admin-file-review-mentions',
                text: 'File Review Mentions',
                path: '/admin/system/file-review-mentions',
                role: 'ItSysAdmin'
              },
              {
                id: 'admin-people-sync-logs',
                text: 'People Sync Logs',
                path: '/admin/people-sync/logs',
                role: 'ItSysAdmin'
              }
            ]
          },
          {
            id: 'admin-custom-apps',
            text: 'Custom Apps',
            role: 'ItSysAdmin',
            items: [
              {
                id: 'admin-hub-custom-apps',
                text: 'HVG Hub Custom Apps',
                path: '/admin/custom-apps',
                role: 'ItSysAdmin'
              },
              {
                id: 'admin-client-custom-apps',
                text: 'Client Custom Apps',
                path: '/admin/client-portal/custom-apps',
                role: 'ItSysAdmin'
              }
            ]
          },
          {
            id: 'admin-our-pharmacies',
            text: 'Our Pharmacies',
            path: '/admin/system/our-pharmacies',
            role: 'ItSysAdmin'
          }
        ]
      }
    ]
  }
];

export function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const username = useAuthStore((s) => s.username);
  const authStatus = useAuthStore((s) => s.status);
  const highestOpsRole = useAuthStore((s) => s.highestOpsRole);
  const mode = useThemeStore((s) => s.mode);
  const toggleMode = useThemeStore((s) => s.toggleMode);
  const showThemeToggle = useThemeStore((s) => s.canToggleMode());
  const {
    hasOpsRole,
    hasAnyHrRole,
    hasSecurePharmReportsAccess,
    hasSettingsPageAccess,
    hasUnreleasedDepartmentNavAccess
  } = useHasRole();

  useActivityHeartbeat({
    enabled: authStatus === 'authenticated',
    themeStorageKey: 'webopsui:theme'
  });
  const { openBugReport, openFeedback, popups } = useSiteSupportPopups(siteFeedbackApi, SITE_SUPPORT_COPY);

  const openWorkspaceTabCount = useEditorWorkspaceStore((s) => s.tabs.length);

  const {
    status: myAppsStatus,
    entries: myAppsEntries,
    entriesById: myAppsEntriesById,
    navEntriesById,
    customAppIds,
    pinnedToolIds,
    recentToolIds
  } = useMyAppsEntries(WEBOPS_MY_APPS_REGISTRY);
  const togglePin = useMyAppsStore((s) => s.togglePin);
  const recordRecent = useMyAppsStore((s) => s.recordRecent);
  const clearRecent = useMyAppsStore((s) => s.clearRecent);
  const resetMyApps = useMyAppsStore((s) => s.reset);

  const recentEntries = useMemo(
    () =>
      recentToolIds.flatMap((toolId) => {
        const entry = myAppsEntriesById.get(toolId);
        return entry ? [entry] : [];
      }),
    [myAppsEntriesById, recentToolIds]
  );

  useEffect(() => {
    if (authStatus === 'anonymous' || authStatus === 'forbidden') resetMyApps();
  }, [authStatus, resetMyApps]);

  useEffect(() => {
    if (myAppsStatus !== 'ready') return;
    const recentId = resolveRecentMyAppId(location.pathname, myAppsEntries);
    if (recentId) void recordRecent(recentId).catch(() => undefined);
  }, [location.pathname, myAppsEntries, myAppsStatus, recordRecent]);

  const navItems = useMemo(() => {
    const workspaceLabel =
      openWorkspaceTabCount > 0 ? `Workspace (${openWorkspaceTabCount})` : 'Workspace';
    const items = NAV_ITEMS.map((item) => {
      if (item.id !== 'files' || !item.items) return item;
      return {
        ...item,
        items: item.items.map((child) =>
          child.id === 'files-workspace' ? { ...child, text: workspaceLabel } : child
        )
      };
    });
    const myAppsLeaves = buildMyAppsSidebarLeaves(
      myAppsStatus === 'ready' ? pinnedToolIds : [],
      navEntriesById,
      myAppsStatus === 'ready' ? customAppIds : []
    ).map((leaf): OpsNavLeaf => {
      const url = leaf.url;
      if (!url) return { id: leaf.id, text: leaf.text, path: leaf.path };
      return {
        id: leaf.id,
        text: leaf.text,
        path: leaf.path,
        onSelect: () => {
          if (openExternalAppUrl(url)) {
            void recordRecent(leaf.id).catch(() => undefined);
            return;
          }
          showErrorToast('Could not open this app.');
        }
      };
    });
    const filesIndex = items.findIndex((item) => item.id === 'files');
    const insertAt = filesIndex < 0 ? items.length : filesIndex + 1;
    const withMyApps: OpsNavLeaf[] = [
      ...items.slice(0, insertAt),
      { id: 'my-apps', text: 'My Apps', items: myAppsLeaves },
      ...items.slice(insertAt)
    ];
    return filterNav(withMyApps, {
      hasOpsRole,
      hasAnyHrRole,
      hasSecurePharmReportsAccess,
      hasSettingsPageAccess,
      hasUnreleasedDepartmentNavAccess
    });
  }, [
    customAppIds,
    hasOpsRole,
    hasAnyHrRole,
    hasSecurePharmReportsAccess,
    hasSettingsPageAccess,
    hasUnreleasedDepartmentNavAccess,
    myAppsStatus,
    navEntriesById,
    openWorkspaceTabCount,
    pinnedToolIds,
    recordRecent
  ]);

  const userMenuItems = useMemo((): HeaderUserMenuItem[] => {
    const items: HeaderUserMenuItem[] = [];
    if (highestOpsRole) {
      items.push({ id: 'role', text: highestOpsRole, rolePill: true, disabled: true });
    }
    items.push({
      id: 'profile',
      text: 'User Profile',
      beginGroup: Boolean(highestOpsRole)
    });
    items.push({
      id: 'settings',
      text: 'User Settings'
    });
    items.push({ id: 'my-tickets', text: 'My Tickets', beginGroup: true });
    items.push({ id: 'feedback', text: 'Feedback' });
    items.push({ id: 'report-bug', text: 'Bug Report' });
    items.push({
      id: 'logout',
      text: 'Sign out',
      beginGroup: true
    });
    return items;
  }, [highestOpsRole]);

  function onUserMenuClick(id: string) {
    if (id === 'logout') window.location.assign(signoutUrl());
    if (id === 'profile') navigate('/settings/profile');
    if (id === 'settings') navigate('/settings/general');
    if (id === 'my-tickets') navigate('/my-tickets');
    if (id === 'feedback') openFeedback();
    if (id === 'report-bug') openBugReport();
  }

  function onMyAppsSelect(result: MyAppsSearchResult) {
    if (!launchMyAppsEntry(result, navigate)) {
      showErrorToast('Could not open this app.');
      return;
    }
    if (result.kind === 'link') void recordRecent(result.id).catch(() => undefined);
  }

  function onTogglePin(toolId: string, pinned: boolean) {
    void togglePin(toolId, pinned).catch((err) =>
      showErrorToast(err instanceof Error ? err.message : 'Could not update pinned apps.')
    );
  }

  function onClearRecent() {
    void clearRecent().catch((err) =>
      showErrorToast(err instanceof Error ? err.message : 'Could not clear recent apps.')
    );
  }

  return (
    <>
      <PlatformAppShell
      storageKeyPrefix="webopsui"
      navItems={navItems}
      useShellPreferencesStore={useShellPreferencesStore}
      header={{
        brand: {
          logoSrc: '/img/ops-hub-logo.webp',
          title: 'HVG Operations Hub'
        },
        username,
        themeMode: mode,
        onThemeToggle: toggleMode,
        showThemeToggle,
        toolbarLeadingActions:
          myAppsStatus === 'ready' && myAppsEntries.length > 0 ? (
            <MyAppsRecentMenu
              recent={recentEntries}
              onSelect={onMyAppsSelect}
              onClearRecent={onClearRecent}
            />
          ) : null,
        userMenuItems,
        onUserMenuClick
      }}
      />
      {popups}
      <MyAppsGlobalExperience
        entries={myAppsEntries}
        pinnedToolIds={pinnedToolIds}
        recentToolIds={recentToolIds}
        onSelect={onMyAppsSelect}
        onTogglePin={onTogglePin}
        onClearRecent={onClearRecent}
      />
      <CookieConsentBanner
        storageKey="webopsui:cookie-consent"
        policyVersion={COOKIE_POLICY_VERSION}
        privacyPath="/settings/privacy"
        cookiesPath="/settings/cookies"
        termsPath="/settings/terms"
        api={siteFeedbackApi}
      />
    </>
  );
}
